import React from 'react';
import { LineChart, Path } from 'react-native-svg-charts';
import { Circle, Line, Defs, ClipPath, Rect } from 'react-native-svg';
import { View, Text } from 'react-native';
import * as shape from 'd3-shape';

import { SmallLoading } from '@components/loading';
import { scale } from '@components/frame';
import { font } from '@styles/fonts';

import { isWeb } from '@helpers';

import { TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { useManageLineChart } from '@components/machines/components/eventsChart/hooks/useManageLineChart';

const LineVertical = (props) => {
  const { x, y, data, displayIndex, C } = props;
  return (
    <>
      <Line y1={y(data[1].data[displayIndex])} y2={'100%'} x1={x(displayIndex)} x2={x(displayIndex)} stroke={C('Gray/Light/4')} strokeWidth={2} />
    </>
  );
};

const Dots = (props) => {
  const { x, y, data, displayIndex, C } = props;
  return (
    <>
      {data.map((i) =>
        i.data.map((value, index) => {
          return (i.showFullLine ? index === i.data.length - 1 : index === displayIndex) ? (
            <Circle key={index} cx={x(index)} cy={y(value)} r={7} stroke={C('Background/Light/Field')} strokeWidth={scale(3)} fill={i.svg.stroke} />
          ) : null;
        })
      )}
    </>
  );
};

const DashedLine = (props) => {
  const { lines, C } = props;
  return (
    <>
      <Path d={lines[1]} stroke={C('Background/Light/Field')} strokeWidth={scale(4)} fill={'none'} clipPath={'url(#clip-path-2)'} />
      <Path d={lines[0]} stroke={C('Gray/Light/3')} strokeWidth={scale(3)} fill={'none'} clipPath={'url(#clip-path-2)'} />
    </>
  );
};

const Clips = ({ x, width, displayIndex }) => (
  <Defs key={'clips'}>
    <ClipPath id="clip-path-1">
      <Rect x={'0'} y={'0'} width={x(displayIndex)} height={'100%'} />
    </ClipPath>
    <ClipPath id={'clip-path-2'}>
      <Rect x={x(displayIndex)} y={'0'} width={width - x(displayIndex)} height={'100%'} />
    </ClipPath>
  </Defs>
);

const LineChartComponent = (props) => {
  const { C } = props;
  const { availableWidth, isLoading, showFullInfo, legend, offset, dataFormatted, chartWidth } = useManageLineChart(props);

  if (isLoading) {
    return (
      <View testID={TEST_IDS.lineChart.loadingContainer} style={{ width: availableWidth }}>
        <View style={{ marginTop: scale(70) }}>
          <SmallLoading />
        </View>
      </View>
    );
  }

  const { legendOffset, bigLegend, colorDots, colorWhiteDots, displayIndex, legendItems, currentLegend } = legend;

  return (
    <View testID={TEST_IDS.lineChart.chartContainer} style={{ width: availableWidth, marginTop: scale(4) }}>
      <LineChart
        testID={TEST_IDS.lineChart.chart}
        style={{ height: scale(165), width: availableWidth }}
        data={dataFormatted}
        contentInset={{
          left: offset,
          right: offset,
          bottom: offset,
          top: offset,
        }}
        curve={shape.curveMonotoneX}
      >
        {!showFullInfo && <Clips {...{ displayIndex, C }} />}
        {!showFullInfo && <DashedLine {...{ displayIndex, C }} />}
        {!showFullInfo && <LineVertical {...{ displayIndex, C }} />}
        {!showFullInfo && <Dots {...{ displayIndex, C }} />}
      </LineChart>
      <View
        testID={TEST_IDS.lineChart.legendContainer}
        style={{
          width: '100%',
          height: scale(7),
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingHorizontal: offset,
        }}
      >
        {legendItems.map((i, index) => {
          const isBig = bigLegend.includes(i);
          const isColor = colorDots.includes(i);
          const isWhiteColor = colorWhiteDots.includes(i);
          const isFirst = index === 0;
          const isLast = index === legendItems.length - 1;
          const isCurrent = currentLegend === i;

          let textWidth = scale(9.1 * i?.length || 0, false);

          const totalItem = legendItems.length - 1;

          const hide =
            !isCurrent &&
            !showFullInfo &&
            ((index === 0 && displayIndex < legendOffset) || (index === totalItem && displayIndex > totalItem - legendOffset));

          return (
            <View
              style={{
                width: chartWidth / legendItems.length,
                maxWidth: scale(2),
                height: scale(2),
                borderRadius: scale(2) / 2,
                backgroundColor: isWhiteColor ? C('Gray/Light/4') : isColor ? C('Gray/Light/1') : 'transparent',
                position: isWeb ? 'initial' : 'relative',
              }}
              key={`${i}-${index}`}
            >
              {isBig && !hide && (
                <Text
                  numberOfLines={1}
                  style={[
                    font['Footnote'],
                    {
                      color: C('Label/Light/Quaternary'),
                      position: 'absolute',
                      top: scale(3),
                      width: textWidth,
                    },
                    isFirst
                      ? {}
                      : isCurrent &&
                        (isWeb
                          ? {
                              textAlign: 'center',
                              marginLeft: -textWidth / 2,
                            }
                          : {
                              right: -scale(textWidth) / 2,
                              textAlign: 'center',
                            }),
                    isLast && {
                      right: scale(0),
                      textAlign: 'right',
                      marginRight: isWeb ? offset : 0,
                    },
                    isFirst && {
                      textAlign: 'left',
                    },
                  ]}
                >
                  {i}
                </Text>
              )}
            </View>
          );
        })}
      </View>
    </View>
  );
};

export default LineChartComponent;
